import Avatar from '@material-ui/core/Avatar'
import React from 'react'
import NotificationsNoneIcon from '@material-ui/icons/NotificationsNone'
import ClearIcon from '@material-ui/icons/Clear'
import IconButton from '@material-ui/core/IconButton'
import clsx from 'clsx'
import { components } from '@plandi/common/src/generated'
import Typography from '@material-ui/core/Typography'
import Badge from '@material-ui/core/Badge'
import { dateFormatMask, format } from '@plandi/common'
import Skeleton from '@material-ui/lab/Skeleton'
import PersonOutlineOutlinedIcon from '@material-ui/icons/PersonOutlineOutlined'
import BusinessOutlinedIcon from '@material-ui/icons/BusinessOutlined'
import MaterialLink from '@material-ui/core/Link'

import { useNotificationItemStyles } from './notification-item.styles'
import { useMutationReadNotification } from '../notifications.mutation'
import { ReactComponent as SubscriptionIcon } from '../../../../assets/icons/subscription.svg'
import { ReactComponent as LockIcon } from '../../../../assets/icons/lock.svg'
import { ReactComponent as StarIcon } from '../../../../assets/icons/star.svg'
import { ReactComponent as UnlockIcon } from '../../../../assets/icons/unlock.svg'
import { ReactComponent as AdminIcon } from '../../../../assets/icons/admin.svg'
import Image from 'next/image'

type NotificationItemProps = components['schemas']['AlertResource'] & {
  isLoading?: boolean
}

export const NotificationItem: React.FC<NotificationItemProps> = (props) => {
  const classes = useNotificationItemStyles()
  const mutationReadNotification = useMutationReadNotification()
  const storageUrl = 'https://plandi-storage.storage.yandexcloud.net/storage/'

  const handleReadNotification = () => {
    if (props.id && !props.isRead) {
      mutationReadNotification.mutate({
        type: props.serviceType,
        notificationId: props.id
      })
    }
  }

  const renderIcon = () => {
    if (props.type === 'newProjectMessage') {
      return props.senderAvatarUrl ? (
        <Avatar className={classes.icon} src={props.senderAvatarUrl} />
      ) : (
        <div
          className={clsx(classes.icon, classes.backgroundGray, classes.name)}
        >
          {props.senderName && props.senderName.substring(0, 1)}
        </div>
      )
    }

    if (props.type === 'adminAction') {
      return (
        <div
          className={clsx(
            classes.icon,
            classes.backgroundBlue,
            classes.fillWhite
          )}
        >
          <AdminIcon />
        </div>
      )
    }

    if (props.type === 'addToFavorite') {
      return (
        <div className={clsx(classes.icon, classes.backgroundGray)}>
          <StarIcon />
        </div>
      )
    }

    if (props.type === 'unBlock') {
      return (
        <div className={clsx(classes.icon, classes.backgroundGray)}>
          <UnlockIcon />
        </div>
      )
    }

    if (props.type === 'block') {
      return (
        <div className={clsx(classes.icon, classes.backgroundRed)}>
          <LockIcon />
        </div>
      )
    }

    if (props.type === 'subscriptionExpires') {
      return (
        <div
          className={clsx(
            classes.icon,
            classes.backgroundBlue,
            classes.fillWhite
          )}
        >
          <SubscriptionIcon />
        </div>
      )
    }

    if (props.type === 'activateStandardSubscription') {
      return (
        <div
          className={clsx(
            classes.icon,
            classes.backgroundRed,
            classes.fillWhite
          )}
        >
          <SubscriptionIcon />
        </div>
      )
    }

    if (props.type === 'activateSubscription') {
      return (
        <div className={clsx(classes.icon, classes.backgroundGray)}>
          <SubscriptionIcon />
        </div>
      )
    }

    if (props.isLoading) {
      return <Skeleton variant="circle" width={40} height={40} />
    }

    if (props.type === 'user' && !props.profileType) {
      return <Avatar className={classes.avatar} />
    }

    if (props.profileType === 'company') {
      return (
        <div className={clsx(classes.icon, classes.iconProfile)}>
          <BusinessOutlinedIcon />
        </div>
      )
    }

    if (props.profileType === 'specialist') {
      return (
        <div className={clsx(classes.icon, classes.iconProfile)}>
          <PersonOutlineOutlinedIcon />
        </div>
      )
    }

    if (props.type === 'default') {
      return (
        <div
          className={clsx(classes.icon, {
            [classes.iconDefault]: props.type === 'default'
          })}
        >
          <NotificationsNoneIcon />
        </div>
      )
    }

    return (
      <div
        className={clsx(classes.icon, {
          [classes.iconDanger]: props.type === 'danger',
          [classes.iconPrimary]: props.type === 'primary'
        })}
      >
        <NotificationsNoneIcon htmlColor="#fff" />
      </div>
    )
  }

  const renderImages = () => {
    return props.content?.files && props.content.files.length >= 1
      ? props.content?.files.map((image, index) => (
          <Image
            alt="img"
            key={index}
            src={storageUrl + image.filename}
            width={29}
            height={29}
          />
        ))
      : null
  }

  const renderContent = () => {
    return (
      props.content?.text && (
        <>
          "{props.content?.text}
          {renderImages()}"
        </>
      )
    )
  }

  return (
    <div
      className={clsx(classes.notificationItem, {
        [classes.notificationItemClose]: !props.isLoading && !props.isRead
      })}
    >
      <div className={classes.main}>
        <div className={classes.avatarContainer}>
          <Badge
            color="primary"
            variant="dot"
            invisible={props.isRead || props.isLoading}
            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
            classes={{ dot: classes.dot }}
          >
            {renderIcon()}
          </Badge>
        </div>
        <Typography
          className={classes.text}
          variant={!props.isRead ? 'subtitle2' : 'body2'}
        >
          {props.url ? (
            <MaterialLink
              href={props.url}
              onClick={handleReadNotification}
              color="textPrimary"
              target="_blank"
            >
              {props.isLoading ? <Skeleton /> : props.text}
            </MaterialLink>
          ) : (
            <>{props.isLoading ? <Skeleton /> : props.text}</>
          )}

          {renderContent()}
        </Typography>
        <div className={classes.dateWrap}>
          <Typography color="textSecondary" align="center" variant="caption">
            {props.isLoading ? (
              <Skeleton width={70} />
            ) : (
              props.createdAt &&
              dateFormatMask(props.createdAt, format.shortFullDate)
            )}
          </Typography>
          <Typography
            className={classes.time}
            color="textSecondary"
            align="center"
            variant="caption"
          >
            {props.isLoading ? (
              <Skeleton width={70} />
            ) : (
              props.createdAt &&
              dateFormatMask(props.createdAt, format.shortFullTime)
            )}
          </Typography>
        </div>
      </div>
      {!props.isLoading && !props.isRead && (
        <IconButton
          aria-label="delete"
          className={classes.close}
          size="small"
          onClick={handleReadNotification}
        >
          <ClearIcon fontSize="inherit" />
        </IconButton>
      )}
    </div>
  )
}
